import { useState } from "react";
import { api } from "../api/client";

const STATES = ["NY", "NJ", "CT", "DE", "PA"];

/**
 * Admin-only: add an external event (FloWrestling / Trackwrestling / any URL)
 * as a published discovered meet. ToS-safe — stores only what the admin types
 * plus a deep link; no scraping. Collapsible to stay out of the way.
 */
export function AddExternalMeet({ onAdded }: { onAdded?: () => void }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({
    title: "",
    sourceUrl: "",
    startDate: "",
    startTime: "",
    state: "NY",
    venueName: "",
    city: "",
    zip: "",
    format: "",
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>();
  const [err, setErr] = useState<string>();

  const set = (k: keyof typeof f, v: string) =>
    setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setErr(undefined);
    setMsg(undefined);
    if (!f.title.trim() || !f.startDate || !f.sourceUrl.trim()) {
      setErr("Title, date, and event URL are required.");
      return;
    }
    setBusy(true);
    try {
      await api.addManualMeet({
        title: f.title.trim(),
        startDate: f.startDate,
        state: f.state,
        sourceUrl: f.sourceUrl.trim(),
        startTime: f.startTime || undefined,
        venueName: f.venueName || undefined,
        city: f.city || undefined,
        zip: f.zip || undefined,
        format: f.format || undefined,
      });
      setMsg(`Added "${f.title}" — now visible on Discover.`);
      setF({
        title: "", sourceUrl: "", startDate: "", startTime: "", state: "NY",
        venueName: "", city: "", zip: "", format: "",
      });
      onAdded?.();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="card">
        <div className="row spread">
          <span className="muted">
            Admin: add a meet from FloWrestling, Trackwrestling, or any link.
          </span>
          <button className="secondary" onClick={() => setOpen(true)}>
            + Add external event
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="row spread">
        <h2 style={{ margin: 0 }}>Add external event</h2>
        <button className="secondary" onClick={() => setOpen(false)}>
          Close
        </button>
      </div>
      <p className="muted">
        Paste the event link (e.g. a FloWrestling page). We store only what you
        enter plus the link — attendees get a “Register / Details” button out to
        the source.
      </p>

      <label>Event URL *</label>
      <input
        placeholder="https://www.flowrestling.org/events/…"
        value={f.sourceUrl}
        onChange={(e) => set("sourceUrl", e.target.value)}
      />
      <label>Title *</label>
      <input
        placeholder="Finger Lakes Duals (8th Grade & Under)"
        value={f.title}
        onChange={(e) => set("title", e.target.value)}
      />
      <div className="row">
        <div style={{ flex: 1, minWidth: 140 }}>
          <label>Date *</label>
          <input
            type="date"
            value={f.startDate}
            onChange={(e) => set("startDate", e.target.value)}
          />
        </div>
        <div style={{ flex: 1, minWidth: 110 }}>
          <label>Start time</label>
          <input
            placeholder="9:00 AM"
            value={f.startTime}
            onChange={(e) => set("startTime", e.target.value)}
          />
        </div>
        <div style={{ width: 90 }}>
          <label>State *</label>
          <select value={f.state} onChange={(e) => set("state", e.target.value)}>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="row">
        <div style={{ flex: 2, minWidth: 160 }}>
          <label>Venue</label>
          <input
            placeholder="Venue name"
            value={f.venueName}
            onChange={(e) => set("venueName", e.target.value)}
          />
        </div>
        <div style={{ flex: 1, minWidth: 120 }}>
          <label>City</label>
          <input value={f.city} onChange={(e) => set("city", e.target.value)} />
        </div>
        <div style={{ width: 100 }}>
          <label>ZIP</label>
          <input value={f.zip} onChange={(e) => set("zip", e.target.value)} />
        </div>
      </div>
      <label>Format</label>
      <input
        placeholder="Duals / Open / Folkstyle / WAR Zone"
        value={f.format}
        onChange={(e) => set("format", e.target.value)}
      />

      <div className="row" style={{ marginTop: "0.75rem" }}>
        <button onClick={submit} disabled={busy}>
          {busy ? "Adding…" : "Add event"}
        </button>
        {msg && <span className="muted">{msg}</span>}
        {err && <span className="error">{err}</span>}
      </div>
      <p className="muted" style={{ fontSize: "0.8rem", marginTop: "0.5rem" }}>
        Tip: adding venue/city/ZIP lets the app show distance from home.
      </p>
    </div>
  );
}
